import { useState, useEffect, useCallback } from 'react'
import { Star } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

type Variant = 'overlay' | 'inline'

interface Props {
  videoId: string
  initialFavorited: boolean
  variant?: Variant
  className?: string
  // Notifies parents that need to react (e.g. Library page removing the row
  // when un-starred). Fires after the optimistic flip; if the request fails
  // and we revert, it fires again with the reverted value.
  onChange?: (favorited: boolean) => void
}

/**
 * Star toggle. Optimistic: flips immediately on click, calls the API, and
 * reverts on failure. Network errors are logged — there's no toast system
 * yet, so a silent revert is the least-jarring UX. The icon and aria-label
 * always reflect the *current* (post-flip) state so screen readers describe
 * what clicking will do, not what it just did.
 *
 * Variants:
 *   - 'overlay' — small floating chip designed to sit on top of a thumbnail.
 *     Always visible (no hover-reveal) so users can star without hunting.
 *   - 'inline' — toolbar-style button matching Copy/Export on the detail page.
 */
export function StarButton({
  videoId,
  initialFavorited,
  variant = 'inline',
  className,
  onChange,
}: Props) {
  const [favorited, setFavorited] = useState(initialFavorited)
  const [pending, setPending] = useState(false)

  // Sync if the parent's source of truth changes (e.g. Library page mounting
  // a fresh row after navigation, or a feed refresh that flips the state).
  useEffect(() => {
    setFavorited(initialFavorited)
  }, [initialFavorited])

  const toggle = useCallback(
    async (e: React.MouseEvent) => {
      // VideoCard wraps the whole tile in an onClick that navigates to
      // /videos/:id. Stop the bubble so starring doesn't also navigate.
      e.preventDefault()
      e.stopPropagation()
      if (pending) return
      const next = !favorited
      setFavorited(next)
      setPending(true)
      onChange?.(next)
      try {
        if (next) await api.favoriteVideo(videoId)
        else await api.unfavoriteVideo(videoId)
      } catch (err) {
        console.error('[star]', err)
        // Revert
        setFavorited(!next)
        onChange?.(!next)
      } finally {
        setPending(false)
      }
    },
    [favorited, videoId, pending, onChange],
  )

  const label = favorited ? 'Remove from Library' : 'Add to Library'

  if (variant === 'overlay') {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white shadow-md backdrop-blur-sm transition-colors hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          favorited && 'bg-amber-500/90 hover:bg-amber-500',
          className,
        )}
      >
        <Star
          className={cn('h-3.5 w-3.5', favorited ? 'fill-current' : 'fill-none')}
        />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      aria-pressed={favorited}
      title={label}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors',
        favorited
          ? 'text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300'
          : 'text-muted-foreground hover:text-foreground',
        className,
      )}
    >
      <Star
        className={cn('h-4 w-4', favorited ? 'fill-current' : 'fill-none')}
      />
      <span>{favorited ? 'Saved' : 'Save'}</span>
    </button>
  )
}
